import { env } from '@utils/env';

/**
 * Matomo (raw tracker / `_paq`) integration — best-effort, fail-safe by design.
 *
 * Every function here is wrapped so that a missing configuration, a blocked or
 * unreachable analytics host (analytics.easybiz.lu), or any unexpected runtime
 * error can NEVER break the app. Analytics is strictly optional.
 *
 * Config comes from runtime env (window.__ENV__) so it can be enabled/disabled
 * per environment without a rebuild. Leave VITE_MATOMO_URL or VITE_MATOMO_SITE_ID
 * empty to completely disable Matomo.
 *
 * Unlike the Tag Manager container, this needs no Matomo UI setup: SPA route
 * changes are tracked entirely here via trackPageView().
 */

type PaqCommand = unknown[];

declare global {
  interface Window {
    _paq?: PaqCommand[];
  }
}

let initialized = false;
let lastTrackedPath: string | null = null;

/** True only when both the base URL and site id are configured. */
const isEnabled = (): boolean =>
  Boolean(env.VITE_MATOMO_URL && env.VITE_MATOMO_SITE_ID);

/** Base URL without a trailing slash, e.g. "https://analytics.easybiz.lu". */
const baseUrl = (): string => (env.VITE_MATOMO_URL ?? '').replace(/\/+$/, '');

/**
 * Bootstraps the Matomo tracker and tracks the initial page load.
 *
 * Safe to call unconditionally: it no-ops when disabled, is idempotent, and
 * swallows any error (including matomo.js failing to load) so the page keeps
 * working even if the analytics host is down or blocked.
 */
export const initMatomo = (): void => {
  try {
    if (initialized) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!isEnabled()) return;

    initialized = true;

    const base = `${baseUrl()}/`;
    const paq = (window._paq = window._paq || []);

    paq.push(['setTrackerUrl', `${base}matomo.php`]);
    paq.push(['setSiteId', env.VITE_MATOMO_SITE_ID]);
    paq.push(['enableLinkTracking']);
    paq.push(['trackPageView']);

    const script = document.createElement('script');
    script.async = true;
    script.src = `${base}matomo.js`;
    // If the analytics host is unreachable/blocked, fail silently.
    script.onerror = () => {
      /* no-op: analytics must never surface errors to the user */
    };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  } catch {
    /* best-effort only — never break the app for analytics */
  }
};

/**
 * Parses a Custom Dimension index from env (a string like "1"). Returns null
 * when unset or not a positive integer, so the dimension is simply skipped.
 */
const dimensionIndex = (raw: string | undefined): number | null => {
  const index = Number(raw);
  return Number.isInteger(index) && index > 0 ? index : null;
};

interface MatomoUser {
  /** Stable identifier — sent as the Matomo User ID. */
  id: string;
  /** Human-readable label (name/email) for the "user" Custom Dimension. */
  name?: string;
  /** Admin role for the "role" Custom Dimension. */
  role?: string;
}

/**
 * Associates the current (authenticated) user with the Matomo visit.
 *
 * Sets the User ID and, when the matching Custom Dimension indexes are
 * configured (VITE_MATOMO_DIMENSION_USER / _ROLE), a readable label and the
 * role. No-ops when Matomo is disabled. The User ID applies to the ongoing
 * visit, so subsequent page views are attributed to this user.
 */
export const setMatomoUser = (user: MatomoUser): void => {
  try {
    if (!isEnabled() || typeof window === 'undefined') return;
    if (!user.id) return;

    const paq = (window._paq = window._paq || []);
    paq.push(['setUserId', user.id]);

    const userDim = dimensionIndex(env.VITE_MATOMO_DIMENSION_USER);
    if (userDim && user.name) {
      paq.push(['setCustomDimension', userDim, user.name]);
    }

    const roleDim = dimensionIndex(env.VITE_MATOMO_DIMENSION_ROLE);
    if (roleDim && user.role) {
      paq.push(['setCustomDimension', roleDim, user.role]);
    }
  } catch {
    /* best-effort only */
  }
};

/**
 * Clears the user context on logout and forces a fresh visit, so the next user
 * on the same browser is not attributed to the previous one. No-ops when
 * Matomo is disabled.
 */
export const resetMatomoUser = (): void => {
  try {
    if (!isEnabled() || typeof window === 'undefined') return;

    const paq = (window._paq = window._paq || []);

    const userDim = dimensionIndex(env.VITE_MATOMO_DIMENSION_USER);
    if (userDim) paq.push(['deleteCustomDimension', userDim]);

    const roleDim = dimensionIndex(env.VITE_MATOMO_DIMENSION_ROLE);
    if (roleDim) paq.push(['deleteCustomDimension', roleDim]);

    paq.push(['resetUserId']);
    // Force a new visit for actions after logout, then stop forcing it.
    paq.push(['appendToTrackingUrl', 'new_visit=1']);
    paq.push(['trackPageView']);
    paq.push(['appendToTrackingUrl', '']);
    lastTrackedPath = null;
  } catch {
    /* best-effort only */
  }
};

/**
 * Tracks an SPA page view.
 *
 * No-ops when Matomo is disabled, and deduplicates consecutive calls for the
 * same path (React Router can notify more than once per navigation). The commands
 * queue in `_paq` even before matomo.js has loaded, so it is safe to call early.
 *
 * @param path the app path used only as the dedupe key; the real URL/title are
 *   read from the live document so Matomo records the exact navigated page.
 */
export const trackPageView = (path: string): void => {
  try {
    if (!isEnabled() || typeof window === 'undefined') return;
    if (path === lastTrackedPath) return;

    lastTrackedPath = path;

    const paq = (window._paq = window._paq || []);
    paq.push(['setCustomUrl', window.location.href]);
    paq.push([
      'setDocumentTitle',
      typeof document !== 'undefined' ? document.title : '',
    ]);
    paq.push(['trackPageView']);
    // Re-scan for links added by the newly rendered route.
    paq.push(['enableLinkTracking']);
  } catch {
    /* best-effort only */
  }
};
